/**
 * Pre-checkout email: session persistence + PostHog for VIP dual funnel.
 * Server almost_purchased still fires from /api/capture-checkout-email after cs_.
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import posthog from "posthog-js";

const STORAGE_KEY = "juaperrone.pre_checkout_email.v1";

export function isValidCheckoutEmail(email: string | null | undefined): boolean {
  const e = (email || "").trim().toLowerCase();
  if (e.length < 5 || !e.includes("@")) return false;
  // Minimal shape: local@domain.tld
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export function normalizeCheckoutEmail(
  email: string | null | undefined,
): string | null {
  const e = (email || "").trim().toLowerCase();
  return isValidCheckoutEmail(e) ? e : null;
}

export function readPreCheckoutEmail(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return normalizeCheckoutEmail(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistPreCheckoutEmail(email: string): void {
  const e = normalizeCheckoutEmail(email);
  if (!e || typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, e);
  } catch {
    /* private mode */
  }
}

/**
 * Prefer Clerk primary, then sessionStorage from a prior modal submit.
 */
export function resolvePreCheckoutEmail(
  clerkEmail?: string | null,
): string | null {
  return (
    normalizeCheckoutEmail(clerkEmail) || readPreCheckoutEmail()
  );
}

export type CheckoutEmailModalAction =
  | "opened"
  | "submitted"
  | "skipped"
  | "closed"
  | "validation_error";

export function trackCheckoutEmailModalPostHog(
  action: CheckoutEmailModalAction,
  props?: {
    surface?: string;
    template?: string;
    close_kind?: string;
    has_email?: boolean;
  },
): void {
  if (!isPostHogConfigured() || typeof window === "undefined") return;
  const payload = {
    site: BRAND_SOURCE_KEY,
    brand_source_key: BRAND_SOURCE_KEY,
    surface: props?.surface,
    template: props?.template,
    close_kind: props?.close_kind,
    has_email: props?.has_email,
    page_path: window.location.pathname,
  };
  try {
    posthog.capture(`checkout_email_modal_${action}`, payload);
    if (action === "submitted") {
      posthog.capture("conversion_funnel_step", {
        ...payload,
        conversion_funnel_step: "email_captured",
        funnel_step: "email_captured",
      });
    }
  } catch {
    /* best-effort */
  }
}
